import React, {useEffect, useState} from "react";
import {Trash2, Edit, Package} from "lucide-react";
import {api} from "../../config/api";
import secureStorage from "../../utils/secureStorage";
import {useQuery, useMutation, useQueryClient} from '@tanstack/react-query';
import ConfirmationDialog from "../Common/ConfirmationDialog";
import {SettingsToolbar, SelectionBar, SettingsTable, StatusBadge, AddAction, ListLoader} from "./SettingsUI";

const UnitsList = ({
                       searchTerm,
                       setSearchTerm,
                       debouncedSearchTerm,
                       selectedItems,
                       setSelectedItems,
                       selectedAll,
                       setSelectedAll,
                       showNotification,
                       onCreateUnit,
                       onEditUnit
                   }) => {
    const queryClient = useQueryClient();
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [itemsToDelete, setItemsToDelete] = useState([]);


    // Function to fetch units from the API
    const fetchUnits = async () => {
        try {
            const accountId = await secureStorage.getItem("selectedAccount");

            const response = await api.get('/units', {
                headers: {
                    'X-account-id': accountId.id,
                    'Accept': 'application/json'
                }
            });

            return response.data || [];
        } catch (error) {
            const errorMessage = error.response?.data?.detail ||
                error.response?.data?.message ||
                "Une erreur lors de la récupération des unités";
            throw new Error(errorMessage);
        }
    };

    // Function to delete units
    const deleteUnits = async (unitIds) => {
        try {
            const accountId = await secureStorage.getItem("selectedAccount");

            const results = await Promise.all(
                unitIds.map(id =>
                    api.delete(`/units/${id}`, {
                        headers: {
                            'X-account-id': accountId.id,
                            'Accept': 'application/json'
                        }
                    }).catch(err => {
                        return {error: err, id};
                    })
                )
            );

            const failures = results.filter(r => r && r.error);
            if (failures.length > 0) {
                if (failures.length === unitIds.length) {
                    throw new Error("Impossible de supprimer les unités sélectionnées");
                } else {
                    throw new Error(`${failures.length} sur ${unitIds.length} unités n'ont pas pu être supprimées`);
                }
            }

            return unitIds;
        } catch (error) {
            const errorMessage = error.response?.data?.detail ||
                error.response?.data?.message ||
                error.message ||
                "Une erreur lors de la suppression des unités";
            throw new Error(errorMessage);
        }
    };

    // Use react-query for delete mutation
    const deleteMutation = useMutation({
        mutationFn: deleteUnits,
        onMutate: () => {
            showNotification('loading', 'Suppression en cours...');
        },
        onSuccess: () => {
            setSelectedItems([]);
            setSelectedAll(false);
            queryClient.invalidateQueries({queryKey: ['units']});
            showNotification('success', 'Unités supprimées avec succès');
            setShowConfirmDialog(false);
            setItemsToDelete([]);
        },
        onError: (error) => {
            showNotification('error', `Erreur: ${error.message}`);
            setShowConfirmDialog(false);
            setItemsToDelete([]);
        }
    });

    // Use react-query to fetch and cache the data
    const {data: units = [], isLoading, error} = useQuery({
        queryKey: ['units', debouncedSearchTerm],
        queryFn: fetchUnits,
    });

    // Filter units based on search term
    const filteredUnits = units.filter(unit =>
        unit.name.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
        unit.code?.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
    );

    // Reset when search term changes
    useEffect(() => {
        setSelectedItems([]);
        setSelectedAll(false);
    }, [debouncedSearchTerm, setSelectedItems, setSelectedAll]);

    // Handle select all checkbox
    const handleSelectAll = () => {
        const newSelectedAll = !selectedAll;
        setSelectedAll(newSelectedAll);
        setSelectedItems(newSelectedAll ? filteredUnits.map(item => item.id) : []);
    };

    // Handle individual item selection
    const toggleItem = (unitId) => {
        setSelectedItems((prevSelected) =>
            prevSelected.includes(unitId)
                ? prevSelected.filter((id) => id !== unitId)
                : [...prevSelected, unitId]
        );
    };

    // ids awaiting confirmation: the selection, or a single row from its menu
    const askDelete = (ids) => {
        setItemsToDelete(ids);
        setShowConfirmDialog(true);
    };

    // Handle delete confirmation
    const handleConfirmDelete = () => {
        deleteMutation.mutate(itemsToDelete);
    };

    // Handle delete cancellation
    const handleCancelDelete = () => {
        setShowConfirmDialog(false);
        setItemsToDelete([]);
    };

    const rowActions = (unit) => [
        {label: 'Modifier', icon: Edit, onClick: () => onEditUnit(unit), tone: 'blue'},
        {label: 'Supprimer', icon: Trash2, onClick: () => askDelete([unit.id]), tone: 'red', danger: true},
    ];

    const columns = [
        {key: 'name', label: 'Nom', render: (u) => (
            <span className="flex items-center gap-1.5 min-w-0">
                <Package className="w-4 h-4 text-gray-500 dark:text-gray-400 flex-shrink-0"/>
                <span className="truncate">{u.name}</span>
            </span>
        )},
        {key: 'code', label: 'Code', render: (u) => u.code || '-'},
        {key: 'value', label: 'Valeur', render: (u) => <span className="tabular-nums">{u.value || '-'}</span>},
        {key: 'active', label: 'Statut', render: (u) => <StatusBadge active={u.active}/>},
    ];

    return (
        <div>
            <AddAction onClick={onCreateUnit}/>
            <SettingsToolbar search={searchTerm} onSearch={setSearchTerm} placeholder="Rechercher une unité..."/>

            <SelectionBar count={selectedItems.length} singular="unité sélectionnée" plural="unités sélectionnées"
                          onDelete={() => askDelete(selectedItems)} deleting={deleteMutation.isPending}/>

            {isLoading ? (
                <ListLoader label="Chargement des unités..."/>
            ) : error ? (
                <div className="p-4 text-center text-red-500">Erreur : {error.message}</div>
            ) : (
                <SettingsTable
                    columns={columns}
                    rows={filteredUnits}
                    selection={{selected: selectedItems, onToggle: toggleItem, allSelected: selectedAll, onToggleAll: handleSelectAll}}
                    rowActions={rowActions}
                    onRowClick={onEditUnit}
                    empty={{
                        icon: Package,
                        title: debouncedSearchTerm ? 'Aucune unité trouvée' : 'Aucune unité',
                        text: debouncedSearchTerm ? 'Ajustez la recherche.' : 'Créez les unités de mesure de vos produits.',
                    }}
                />
            )}

            {showConfirmDialog && (
                <ConfirmationDialog
                    title="Confirmation de suppression"
                    message={`Êtes-vous sûr de vouloir supprimer ${itemsToDelete.length} unité${itemsToDelete.length > 1 ? 's' : ''} ? Cette action est irréversible.`}
                    confirmLabel="Supprimer"
                    cancelLabel="Annuler"
                    onConfirm={handleConfirmDelete}
                    onCancel={handleCancelDelete}
                    danger={true}
                />
            )}
        </div>
    );
};

export default UnitsList;

